import { AuraType } from "../types/game";

/* ─── Paliers de ligue (Bronze → Diamant) ──────────────────────────────────
   On monte d'une ligue chaque semaine en finissant 1er de sa poule.
   Les seuils ELO indiquent le palier minimum pour appartenir à chaque ligue. */
export interface LeagueTier {
  key: string;
  label: string;
  minElo: number;
  color: string;
  emoji: string;
}

export const LEAGUE_TIERS: LeagueTier[] = [
  { key: "bronze", label: "Bronze", minElo: 0, color: "#CD7F32", emoji: "🥉" },
  { key: "silver", label: "Silver", minElo: 800, color: "#C0C0C0", emoji: "🥈" },
  { key: "gold", label: "Gold", minElo: 1200, color: "#F5B301", emoji: "🥇" },
  { key: "platine", label: "Platinum", minElo: 1600, color: "#5BC8D6", emoji: "💠" },
  { key: "rubis", label: "Ruby", minElo: 2000, color: "#E0115F", emoji: "🔻" },
  { key: "emeraude", label: "Emerald", minElo: 2400, color: "#2DE36B", emoji: "💚" },
  { key: "diamant", label: "Diamond", minElo: 2800, color: "#7DD3FC", emoji: "💎" },
];

/** Renvoie le palier courant + index + palier suivant (null si déjà au top). */
export function getTier(elo: number): {
  tier: LeagueTier;
  index: number;
  next: LeagueTier | null;
} {
  let index = 0;
  for (let i = 0; i < LEAGUE_TIERS.length; i++) {
    if (elo >= LEAGUE_TIERS[i].minElo) index = i;
  }
  return {
    tier: LEAGUE_TIERS[index],
    index,
    next: LEAGUE_TIERS[index + 1] ?? null,
  };
}

/* ─── Affichage des orbes ──────────────────────────────────────────────── */
export const ORB_EMOJI: Record<AuraType, string> = {
  ROUGE: "🔴",
  BLEUE: "🔵",
  VERTE: "🟢",
};

// PNG sans fond (CDN postimg)
export const ORB_IMG: Record<AuraType, string> = {
  ROUGE: "https://i.postimg.cc/ZKL3rF1c/Red-orb-(1).png",
  BLEUE: "https://i.postimg.cc/Qxq5pkR6/Bleu-orb-(1).png",
  VERTE: "https://i.postimg.cc/L6VfzBrx/Green-orb-(1).png",
};

export const EFFECT_ICONS: Record<string, string> = {
  precision: "🎯",
  tackle_safety: "🛡️",
  goalkeeper_buff: "🧤",
  counter_chance: "⚡",
  build_up: "🔺",
  enemy_debuff: "🚌",
};

/* ─── Calendrier hebdomadaire ──────────────────────────────────────────── */
export const MATCHES_PER_WEEK = 7;

/** Lundi 00:00 de la semaine contenant `d`. */
export function getWeekStart(d: Date = new Date()): Date {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = lundi
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Jours restants avant la fin de la semaine de ligue. */
export function daysUntilWeekEnd(now: Date = new Date()): number {
  const start = getWeekStart(now);
  const end = new Date(start);
  end.setDate(end.getDate() + MATCHES_PER_WEEK);
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
}

/* ─── Semaine de mini-ligue (Dimanche 16h → Dimanche suivant 16h) ───────────
   La poule de 7 jours redémarre chaque Dimanche à 16h : les statistiques
   (W/D/L, points…) sont remises à zéro à cette frontière. */
export const LEAGUE_WEEK_RESET_HOUR = 16;

/** Dernier Lundi 16h00 (début de la mini-ligue en cours).
 *  Schedule : Lun→Dim, tous les jours à 16h. Dimanche = dernier match.
 *  Nouveau reset le Lundi suivant à 16h → 24h de pause entre Dim et Lun. */
export function getLeagueWeekStart(d: Date = new Date()): Date {
  const start = new Date(d);
  // getDay() : 0=Dim, 1=Lun … 6=Sam → daysToMonday = (getDay()+6) % 7
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  start.setHours(LEAGUE_WEEK_RESET_HOUR, 0, 0, 0);
  // Si on est Lundi avant 16h, la ligue courante a commencé le Lundi précédent.
  if (start.getTime() > d.getTime()) start.setDate(start.getDate() - 7);
  return start;
}

/** Jours restants avant le reset de la mini-ligue (Dimanche 16h). */
export function daysUntilLeagueWeekEnd(now: Date = new Date()): number {
  const start = getLeagueWeekStart(now);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
}
